/* oxlint-disable @typescript-eslint/no-shadow */
import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { atomWithDefault } from 'jotai/utils';
import { createContext } from 'react';

import {
  getTradingLeaderboardsApi,
  getTradingLeaderboardsMeApi,
  getTradingLeaderboardsTierApi,
  putTradingLeaderboardsOptOutApi,
} from '@flipster/apis/v2/trading-leaderboards';
import {
  TradingLeaderboardInterval,
  TradingLeaderboardMyRankingItem,
  TradingLeaderboardTier,
} from '@flipster/apis/v2/trading-leaderboards/types';
import { overlayBunja } from '@flipster/overlay';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import { getOrdinalNumber } from '@flipster/utils/num';
import segment, { SegmentDataType } from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { authBunja } from '../shared/auth/state';
import { SettingsOverlay } from './modal/settings';
import { tierInfos } from './shared/constants';

export interface LeaderboardContext {
  interval: TradingLeaderboardInterval;
  tier: TradingLeaderboardTier;
}
export const LeaderboardContext = createContext<LeaderboardContext>({ interval: 'WEEKLY', tier: 'ALL' });
export const LeaderboardScope = createScopeFromContext(LeaderboardContext);

export const GetTradingLeaderboardsQueryKey = 'getTradingLeaderboardsApi';
export const GetTradingLeaderboardsMeQueryKey = 'getTradingLeaderboardsMeApi';

export const tradingLeaderboardMyRankingBunja = bunja(() => {
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const getTradingLeaderboardsMeAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: [GetTradingLeaderboardsMeQueryKey],
      queryFn: getTradingLeaderboardsMeApi,
      enabled: isCookieLoggedIn,
    };
  });
  // [
  //   { tier: ALL,                 interval: WEEKLY  },
  //   { tier: ALL,                 interval: MONTHLY },
  //   { tier: <user_current_tier>, interval: WEEKLY  },
  //   { tier: <user_current_tier>, interval: MONTHLY },
  // ]
  const myRankingsAtom = atom((get) => get(getTradingLeaderboardsMeAtom).data?.data?.rankings);
  // every optOut field in rankings is same. need to be migrated after field depth changed.
  // context: https://prextech.slack.com/archives/C087CLN6AKD/p1750047813909119?thread_ts=1750047032.306429&cid=C087CLN6AKD
  const optOutAtom = atom((get) => get(myRankingsAtom)?.[0]?.optOut ?? false);
  const nicknameAtom = atom((get) => get(myRankingsAtom)?.[0]?.nickname);
  const myIntervalRankingsAtom = atom<Record<TradingLeaderboardInterval, TradingLeaderboardMyRankingItem | undefined>>(
    (get) => {
      const rankings = get(myRankingsAtom) ?? [];
      const weekly = rankings.find((r) => r.league === 'TIERED' && r.interval === 'WEEKLY' && r.tier !== 'ALL');
      const monthly = rankings.find((r) => r.league === 'TIERED' && r.interval === 'MONTHLY' && r.tier !== 'ALL');
      return { WEEKLY: weekly, MONTHLY: monthly };
    },
  );
  const myOptInIntervalRankingsAtom = atom<
    Record<TradingLeaderboardInterval, TradingLeaderboardMyRankingItem | undefined>
  >((get) => {
    const myIntervalRankings = get(myIntervalRankingsAtom);
    return {
      WEEKLY: myIntervalRankings.WEEKLY?.optOut == false ? myIntervalRankings.WEEKLY : undefined,
      MONTHLY: myIntervalRankings.MONTHLY?.optOut == false ? myIntervalRankings.MONTHLY : undefined,
    };
  });
  return {
    optOutAtom,
    nicknameAtom,
    myRankingsAtom,
    myIntervalRankingsAtom,
    myOptInIntervalRankingsAtom,
  };
});
tradingLeaderboardMyRankingBunja.debugLabel = 'tradingLeaderboardMyRankingBunja';

export const tradingLeaderboardBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlay = bunja.use(overlayBunja);
  const { optOutAtom } = bunja.use(tradingLeaderboardMyRankingBunja);
  // league onboarding modal
  const leagueOnboardingModalAtom = atomWithDefault(() => {
    const key = 'flipster.configs.v1.leaderboardLeagueOnboardingModal';
    return getLocalStorage(key) ?? (() => (setLocalStorage(key, false), true))();
  });
  // tier descriptions
  const tierDescriptionsBaseAtom = atomWithQuery(() => ({
    queryKey: ['getTradingLeaderboardsTierApi'],
    queryFn: () => getTradingLeaderboardsTierApi(),
  }));
  const tierDescriptionsAtom = atom((get) => get(tierDescriptionsBaseAtom).data?.data.descriptions);
  // functions
  function openSettings(segmentPayload: SegmentDataType['leaderboard_setting_click']) {
    segment.track(TradingLeaderboardEvent.LeaderboardSettingClick, segmentPayload);
    overlay.open(({ isOpen, onClose, unmount }) => (
      <SettingsOverlay
        initialSettings={{ optOut: store.get(optOutAtom) }}
        onFinish={onClose}
        isOpen={isOpen}
        onClose={onClose}
        onExit={unmount}
      />
    ));
  }
  return {
    leagueOnboardingModalAtom,
    tierDescriptionsAtom,
    openSettings,
    openProfileSettings() {
      openSettings({ from_route: 'setting_button' });
    },
  };
});
tradingLeaderboardBunja.debugLabel = 'tradingLeaderboardBunja';

export const intervalLeaderboardBunja = bunja(() => {
  const { interval, tier } = bunja.use(LeaderboardScope);
  const getTradingLeaderboardsAtom = atomWithQuery(() => ({
    queryKey: [GetTradingLeaderboardsQueryKey, interval, tier],
    queryFn: () => getTradingLeaderboardsApi({ interval, tier }),
  }));
  const rankingsAtom = atom((get) => get(getTradingLeaderboardsAtom).data?.data?.rankings);
  const lastUpdatedAtAtom = atom((get) => get(getTradingLeaderboardsAtom).data?.data?.lastUpdatedAt);
  const competitionEndAtAtom = atom((get) => get(getTradingLeaderboardsAtom).data?.data?.competitionEndAt);
  const currentTierDescriptionAtom = atom(
    (get) => get(getTradingLeaderboardsAtom).data?.data.currentTierDescription ?? '',
  );

  return {
    interval,
    tier,
    currentTierInfo: tierInfos[tier],
    rankingsAtom,
    lastUpdatedAtAtom,
    competitionEndAtAtom,
    currentTierDescriptionAtom,
  };
});
intervalLeaderboardBunja.debugLabel = 'intervalLeaderboardBunja';

export const intervalLeaderboardMyRankingBunja = bunja(() => {
  const { myRankingsAtom, myIntervalRankingsAtom } = bunja.use(tradingLeaderboardMyRankingBunja);
  const { interval, tier } = bunja.use(LeaderboardScope);
  const putTradingLeaderboardsOptOutAtom = atomWithMutation((get) => ({
    mutationKey: ['putTradingLeaderboardsOptOutAtom'],
    mutationFn: putTradingLeaderboardsOptOutApi,
    async onSettled() {
      const queryClient = get(queryClientAtom);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GetTradingLeaderboardsQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [GetTradingLeaderboardsMeQueryKey] }),
      ]);
    },
    onError(error) {
      snackbar.error({ message: getErrorMessage(error) });
    },
  }));
  const allRankingAtom = atom((get) => get(myRankingsAtom)?.find((r) => r.tier == 'ALL' && r.interval == interval));
  const intervalRankingAtom = atom((get) => get(myIntervalRankingsAtom)[interval]);
  const currentCursorRankingAtom = atom((get) => {
    if (tier == 'ALL') return get(allRankingAtom);
    const myRanking = get(intervalRankingAtom);
    if (myRanking?.tier === tier) return myRanking;
  });
  return {
    intervalRankingAtom,
    currentCursorRankingAtom,
    putTradingLeaderboardsOptOutAtom,
  };
});
intervalLeaderboardMyRankingBunja.debugLabel = 'intervalLeaderboardMyRankingBunja';

export function formatRank(rank: number, lastRank: number): string;
export function formatRank(rank: undefined, lastRank: number): undefined;
export function formatRank(rank: number | undefined, lastRank: number): string | undefined;
export function formatRank(rank: number | undefined, lastRank: number) {
  const lastRankLabel = `${lastRank}+`;
  if (rank == null) return;
  return rank > lastRank ? lastRankLabel : `${rank}${getOrdinalNumber(rank).suffix}`;
}
